import { Control, FieldValues, Path, useController } from "react-hook-form";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import {
  Combobox,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "components/ui/combobox";
import type { Option } from "components/ui/combobox/types";
import UserName, { formatUserNameWithStatus } from "components/users/UserName";
import { useCompany } from "lib/hooks/api/company/useCompany";
import UIcon from "lib/icons/untitled_ui/UIcon";
import UserInitials from "./UserInitials";

type RHFProps<T extends FieldValues> = {
  control: Control<T>;
  name: Path<T>;
};

type ControlledProps = {
  value: string | null | undefined;
  onChange: (value: string | null) => void;
};

export type CompanyUserComboBoxProps<TFieldValues extends FieldValues = FieldValues> = (
  | RHFProps<TFieldValues>
  | ControlledProps
) & {
  placeholder?: string;
  disabled?: boolean;
  truncate?: boolean;
  trigger?: React.ReactNode;
};

function CompanyUserOption({ icon, text }: { icon: React.ReactNode; text: React.ReactNode }) {
  return (
    <Flex alignItems="center" gap={8}>
      {icon}
      <Text>{text}</Text>
    </Flex>
  );
}

function UserUnassignedIcon({ size = 16 }: { size?: number }) {
  return <UIcon icon="userPlus01" size={size} color="sysTextPlaceholder" />;
}

function CompanyUserComboboxInner({
  value,
  onChange,
  placeholder,
  disabled,
  truncate,
  trigger,
}: {
  value: string | null | undefined;
  onChange: (value: string | null) => void;
  placeholder: string;
  disabled: boolean;
  truncate: boolean;
  trigger?: React.ReactNode;
}) {
  const { data: company, isLoading: isLoadingCompany } = useCompany({ includeInactive: true });

  // Hide deactivated users from the options list, BUT keep the currently-selected user even
  // if they've since been deactivated, so the picker can still display the previously-assigned value.
  const visibleUsers =
    company?.users.filter((user) => user.active !== false || user.guid === value) ?? [];

  const options: Option[] = [
    { label: "Unassigned", value: "" },
    ...visibleUsers.map((user) => ({
      value: user.guid,
      label: formatUserNameWithStatus(user),
    })),
  ];

  const getUser = (guid: string) => {
    if (!guid || !company) return null;
    return company.users.find((user) => user.guid === guid);
  };

  // Convert single value to array for Combobox (expects array)
  // null = actively unassigned → map to [""] so the Unassigned option appears selected
  // undefined = never set → map to [] so placeholder is shown
  const arrayValue = value === null ? [""] : value ? [value] : [];

  return (
    <Combobox
      items={options}
      value={arrayValue}
      onChange={(values) => {
        const newValue = values.length > 0 ? String(values[0]) : null;
        onChange(newValue === "" ? null : newValue);
      }}
      multiple={false}
    >
      {(combobox) => {
        const selectedValue = combobox.value[0];
        const selectedUser =
          selectedValue && selectedValue !== "" ? getUser(String(selectedValue)) : null;

        const handleTriggerClick = () => {
          if (!combobox.open) {
            combobox.setFilterValue(""); // Clear search when opening
          }
          combobox.setOpen(!combobox.open);
        };

        return (
          <>
            <ComboboxContent
              trigger={
                trigger ? (
                  <div onClick={handleTriggerClick}>{trigger}</div>
                ) : (
                  <CustomTrigger truncate={truncate} onClick={handleTriggerClick}>
                    {selectedUser ? (
                      <>
                        <UserInitials
                          firstName={selectedUser.firstName}
                          lastName={selectedUser.lastName}
                          showTooltip={false}
                          type="small"
                        />
                        <TriggerText truncate={truncate}>
                          <UserName user={selectedUser} />
                        </TriggerText>
                      </>
                    ) : (
                      <>
                        <UserUnassignedIcon />
                        <TriggerText truncate={truncate}>
                          {selectedValue === "" ? "Unassigned" : placeholder}
                        </TriggerText>
                      </>
                    )}
                  </CustomTrigger>
                )
              }
              open={combobox.open}
              setOpen={combobox.setOpen}
            >
              <ComboboxInput
                placeholder="Search by username"
                filterValue={combobox.filterValue}
                setFilterValue={combobox.setFilterValue}
                open={combobox.open}
                setOpen={combobox.setOpen}
                value={[]} // Pass empty array to prevent showing selected option text
                items={combobox.items}
                multiple={combobox.multiple}
                inputRef={combobox.inputRef}
                inputElementRef={combobox.inputElementRef}
                disabled={disabled || isLoadingCompany}
              />
              <ComboboxEmpty
                filteredItems={combobox.filteredItems}
                filterValue={combobox.filterValue}
                disableAddingOptions
              >
                No users found
              </ComboboxEmpty>
              <ComboboxList items={combobox.items} filteredItems={combobox.filteredItems}>
                <ComboboxCollection options={combobox.filteredItems as Option[]}>
                  {(option: Option) => {
                    const user = option.value !== "" ? getUser(String(option.value)) : null;
                    return (
                      <ComboboxItem
                        key={String(option.value)}
                        option={option}
                        value={combobox.value}
                        selectItem={combobox.selectItem}
                        multiple={combobox.multiple}
                        highlightedIndex={combobox.highlightedIndex}
                        filteredItems={combobox.filteredItems}
                      >
                        {user ? (
                          <CompanyUserOption
                            text={<UserName user={user} />}
                            icon={
                              <UserInitials
                                firstName={user.firstName}
                                lastName={user.lastName}
                                showTooltip={false}
                                type="small"
                              />
                            }
                          />
                        ) : (
                          <Flex alignItems="center" gap={8}>
                            <UnassignedIconOption>
                              <UserUnassignedIcon size={24} />
                            </UnassignedIconOption>
                            <Text>Unassigned</Text>
                          </Flex>
                        )}
                      </ComboboxItem>
                    );
                  }}
                </ComboboxCollection>
              </ComboboxList>
            </ComboboxContent>
          </>
        );
      }}
    </Combobox>
  );
}

function CompanyUserComboboxRHF<T extends FieldValues>({
  control,
  name,
  placeholder,
  disabled,
  truncate,
  trigger,
}: RHFProps<T> & {
  placeholder: string;
  disabled: boolean;
  truncate: boolean;
  trigger?: React.ReactNode;
}) {
  const { field } = useController({ control, name });
  return (
    <CompanyUserComboboxInner
      value={field.value}
      onChange={field.onChange}
      placeholder={placeholder}
      disabled={disabled}
      truncate={truncate}
      trigger={trigger}
    />
  );
}

export function CompanyUserCombobox<TFieldValues extends FieldValues = FieldValues>(
  props: CompanyUserComboBoxProps<TFieldValues>,
) {
  const { placeholder = "Select user", disabled = false, truncate = false, trigger } = props;

  if ("control" in props) {
    return (
      <CompanyUserComboboxRHF
        control={props.control}
        name={props.name}
        placeholder={placeholder}
        disabled={disabled}
        truncate={truncate}
        trigger={trigger}
      />
    );
  }

  return (
    <CompanyUserComboboxInner
      value={props.value}
      onChange={props.onChange}
      placeholder={placeholder}
      disabled={disabled}
      truncate={truncate}
      trigger={trigger}
    />
  );
}

const CustomTrigger = styled.button<{ truncate: boolean }>(({ theme, truncate }) => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  width: "100%",
  padding: "5px 10px",
  height: 32,
  borderRadius: 4,
  backgroundColor: theme.colors.sysBackgroundDefault,
  border: "none",
  cursor: "pointer",
  fontSize: 14,
  color: theme.colors.sysTextDefault,
  transition: "background-color 0.2s",
  textAlign: "left",
  // Opt-in: allow the trigger to shrink below its content size when placed inside a
  // width-constrained container (e.g. a table cell), so TriggerText's ellipsis activates.
  ...(truncate && { minWidth: 0 }),

  "&:hover": {
    backgroundColor: theme.colors.sysTextDefault + "0A", // 4% opacity
  },

  "&:active": {
    backgroundColor: theme.colors.sysTextDefault + "14", // 8% opacity
  },

  "&:focus-visible": {
    outline: `2px solid ${theme.colors.sysPrimaryDefault}`,
    // Inset the ring when truncating so an ancestor's `overflow: hidden`
    // (e.g. width-constrained table cells) doesn't clip the focus indicator.
    outlineOffset: truncate ? -2 : 2,
  },
}));

const TriggerText = styled.span<{ truncate: boolean }>(({ truncate }) => ({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  ...(truncate && { minWidth: 0 }),
}));

const UnassignedIconOption = styled.div({
  display: "flex",
  alignItems: "center",
  width: 24,
  height: 24,
  paddingLeft: 2,
});
